// FILE: QueuedComposerTurnsControl.tsx
// Purpose: The bar above the composer that says how many messages are waiting, and the popover it
//   opens to manage them.
// Layer: Chat composer presentation
// Depends on: the composer queue contract, the queue summary helper, and caller-owned callbacks.
//
// The bar is the whole control: there is no icon in the composer's action row. An icon there sat in
// the same cramped slot as Stop and the microphone, and its badge overlapped the Stop button while
// a turn was running -- which is precisely when the queue fills up. A full-width bar has room to
// say what it holds, and it disappears with the queue so an empty queue costs no pixels at all.

import { memo } from "react";
import type { ComposerQueuedMessage } from "@taskin-code/contracts";
import { PiArrowBendDownRight } from "react-icons/pi";

import type { QueuedComposerTurnsSummary } from "../ChatView.logic";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { QueuedComposerTurnRow } from "./QueuedComposerTurnRow";

export const QueuedComposerTurnsControl = memo(function QueuedComposerTurnsControl(props: {
  summary: QueuedComposerTurnsSummary;
  items: ReadonlyArray<ComposerQueuedMessage>;
  pendingIds: ReadonlyArray<string>;
  error: string | null;
  hasLiveTurn: boolean;
  /** False when the composer is empty or the thread is still a local draft. */
  canSaveDraft: boolean;
  /** Why saving is unavailable, shown as the footer button's tooltip. */
  saveDraftHint?: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveDraft: () => void;
  onSetAutoSend: (queuedId: string, autoSend: boolean) => void;
  onSend: (queuedId: string) => void;
  onSteer: (queuedId: string) => void;
  onEdit: (item: ComposerQueuedMessage) => void;
  onRemove: (queuedId: string) => void;
}) {
  // Nothing queued means no bar at all. Ctrl+S still reaches the queue from an empty composer, so
  // hiding it costs no capability -- and the bar reappears the moment it holds something.
  if (props.summary.total === 0) {
    return null;
  }

  return (
    <Popover open={props.open} onOpenChange={props.onOpenChange}>
      <div className="mx-auto flex w-11/12 flex-col">
        <PopoverTrigger
          render={
            <button
              type="button"
              data-testid="queued-follow-ups-trigger"
              className="chat-composer-surface flex items-center gap-2 rounded-t-2xl border border-b-0 border-border/60 bg-card px-2.5 py-2 text-left text-[12px] transition-colors hover:bg-muted/40"
            />
          }
        >
          <PiArrowBendDownRight
            aria-hidden="true"
            className="size-3 shrink-0 text-muted-foreground/70"
          />
          <span className="truncate text-[12px] font-medium text-foreground/85">
            {props.summary.label}
          </span>
        </PopoverTrigger>
      </div>

      <PopoverPopup side="top" align="start" className="w-80 p-0">
        <div className="flex flex-col gap-1 py-1">
          <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Queued messages
          </div>

          {props.error !== null ? (
            <p className="px-2 pb-1 text-[11px] leading-snug text-destructive">{props.error}</p>
          ) : null}

          <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
            {props.items.map((item) => (
              <QueuedComposerTurnRow
                key={item.queuedId}
                item={item}
                pending={props.pendingIds.includes(item.queuedId)}
                hasLiveTurn={props.hasLiveTurn}
                onSetAutoSend={props.onSetAutoSend}
                onSend={props.onSend}
                onSteer={props.onSteer}
                onEdit={props.onEdit}
                onRemove={props.onRemove}
              />
            ))}
          </div>

          <div className="border-t border-border/60 px-2 pt-1.5">
            <button
              type="button"
              className="w-full rounded-lg px-2 py-1 text-left text-[12px] font-medium text-foreground transition-colors hover:bg-muted/60 disabled:pointer-events-none disabled:opacity-50"
              disabled={!props.canSaveDraft}
              title={props.saveDraftHint}
              data-testid="queued-follow-ups-save-draft"
              onClick={props.onSaveDraft}
            >
              Save draft to queue (Ctrl+S)
            </button>
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
});
